import { BranchTag } from "@/components/BranchTag";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, PlayCircle, Eye, Ban, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";

import { confirmAction } from "@/lib/dialogs";
const WRITE_ROLES = ["ORG_ADMIN", "STORES", "PROCUREMENT"];

export default function ProductionRuns() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [runs, setRuns] = useState([]);
  const [plants, setPlants] = useState([]);
  const [boms, setBoms] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: p }, { data: b }, { data: w }, { data: it }] = await Promise.all([
        api.get("/production-runs"), api.get("/production-plants"), api.get("/boms"), api.get("/inventory/warehouses"), api.get("/inventory/items"),
      ]);
      setRuns(r); setPlants(p); setBoms(b); setWarehouses(w); setItems(it);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const plantName = (id) => plants.find((p) => p.id === id)?.name || "—";
  const itemLabel = (id) => { const it = items.find((i) => i.id === id); return it ? `${it.code} — ${it.name}` : "—"; };
  const bomLabel = (id) => { const b = boms.find((x) => x.id === id); return b ? `${itemLabel(b.finished_item_id)} (v${b.version})` : "—"; };

  const cancelRun = async (run) => {
    if (!await confirmAction(`Cancel production run ${run.number}? Its finished goods leave the store and the raw materials come back.`)) return;
    setBusyId(run.id);
    try {
      await api.post(`/production-runs/${run.id}/cancel`);
      toast.success(`${run.number} cancelled`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const post = async (run) => {
    setBusyId(run.id);
    try {
      await api.post(`/production-runs/${run.id}/post`);
      toast.success("Production run posted — inventory consumed, finished goods received, GL updated");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="production-runs-page">
      <PageHeader
        title="Production Runs"
        description="Post a run to consume BOM components from stock, receive finished goods, and book the cost to GL."
        action={<div className="flex flex-wrap gap-2">
          <PlantCostDialog companies={companies} />
          {canWrite && <CreateRunDialog companies={companies} plants={plants} boms={boms} warehouses={warehouses} onCreated={load} />}
        </div>}
      />

      <div className="rounded-xl border border-border bg-card">
        {!loading && runs.length === 0 ? (
          <EmptyState title="No production runs yet" description="Create a plant and a BOM first, then start a run." testId="production-runs-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Number</TableHead><TableHead>Plant</TableHead><TableHead>BOM</TableHead><TableHead className="text-right">Qty produced</TableHead><TableHead className="text-right">Total cost</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id} data-testid={`production-run-row-${r.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{r.number}<BranchTag row={r} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{plantName(r.plant_id)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{bomLabel(r.bom_id)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{fmtNum(r.qty_produced, 3)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{fmtNum(r.total_cost, 2)}</TableCell>
                  <TableCell><StatusPill value={r.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <RunDetailDialog run={r} itemLabel={itemLabel} />
                      {canWrite && r.status === "POSTED" && (
                        <Button size="sm" variant="ghost" title="Cancel run" disabled={busyId === r.id} onClick={() => cancelRun(r)} data-testid={`cancel-production-run-${r.number}`}>
                          <Ban className="h-3.5 w-3.5 text-rose-400" />
                        </Button>
                      )}
                      {canWrite && r.status === "DRAFT" && (
                        <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => post(r)} data-testid={`post-production-run-button-${r.number}`}>
                          {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />} Post
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function RunDetailDialog({ run, itemLabel }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`view-run-consumption-button-${run.number}`}><Eye className="h-3.5 w-3.5" /></Button></DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`run-consumption-dialog-${run.number}`}>
        <DialogHeader><DialogTitle>{run.number} — BOM consumption</DialogTitle></DialogHeader>
        {run.consumption.length === 0 ? (
          <p className="text-xs text-muted-foreground">No components.</p>
        ) : (
          <div className="space-y-1.5">
            {run.consumption.map((c, idx) => (
              <div key={idx} className="flex items-center justify-between rounded-lg border border-border p-2 text-xs" data-testid={`consumption-line-${idx}`}>
                <span className="text-muted-foreground">{itemLabel(c.item_id)}</span>
                <span className="font-mono font-semibold text-foreground">{fmtNum(c.quantity, 4)}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateRunDialog({ companies, plants, boms, warehouses, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", plant_id: "", bom_id: "", warehouse_id: "", run_date: todayISO(), qty_produced: "", overhead_per_unit: "0" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/production-runs", form);
      toast.success("Production run created as a draft");
      setForm({ company_id: "", plant_id: "", bom_id: "", warehouse_id: "", run_date: todayISO(), qty_produced: "", overhead_per_unit: "0" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-production-run-button"><Plus className="h-4 w-4" /> New Run</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Production Run</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-production-run-form">
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="run-company-select" />
            <div>
              <Label>Plant</Label>
              <Select required value={form.plant_id} onValueChange={(v) => setForm({ ...form, plant_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="run-plant-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>BOM</Label>
              <Select required value={form.bom_id} onValueChange={(v) => setForm({ ...form, bom_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="run-bom-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{boms.map((b) => <SelectItem key={b.id} value={b.id}>v{b.version} — {b.components.length} comp.</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Warehouse</Label>
              <Select required value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="run-warehouse-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Run date</Label><Input required type="date" value={form.run_date} onChange={set("run_date")} className="mt-1.5" data-testid="run-date-input" /></div>
            <div><Label>Qty produced</Label><Input required type="number" step="0.001" value={form.qty_produced} onChange={set("qty_produced")} className="mt-1.5" data-testid="run-qty-input" /></div>
            <div><Label>Overhead/unit</Label><Input type="number" step="0.01" value={form.overhead_per_unit} onChange={set("overhead_per_unit")} className="mt-1.5" data-testid="run-overhead-input" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-production-run-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PlantCostDialog({ companies }) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", date_from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), date_to: iso(now) });
  const [data, setData] = useState(null);
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const run = async () => {
    const company = form.company_id || companies?.[0]?.id;
    if (!company) return;
    try {
      const { data: res } = await api.get("/production/plant-cost", { params: { ...form, company_id: company } });
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const diff = Number(data?.difference || 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) run(); }}>
      <DialogTrigger asChild><Button variant="outline" data-testid="plant-cost-button"><BarChart3 className="h-4 w-4" /> Plant cost report</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>Plant cost report</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label>From</Label><Input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} className="mt-1.5" /></div>
          <div><Label>To</Label><Input type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} className="mt-1.5" /></div>
          <Button onClick={run}>Show</Button>
        </div>
        {data && (
          <div className="space-y-3 text-sm">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground"><tr>
                <th className="py-1 text-left font-medium">Plant</th><th className="text-right font-medium">Runs</th><th className="text-right font-medium">Qty</th>
                <th className="text-right font-medium">Materials</th><th className="text-right font-medium">Overhead</th><th className="text-right font-medium">Cost / unit</th>
              </tr></thead>
              <tbody>
                {data.plants.length === 0 && <tr><td colSpan={6} className="py-3 text-center text-muted-foreground">No posted runs in this period</td></tr>}
                {data.plants.map((p, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="py-1.5">{p.plant}</td><td className="text-right">{p.runs}</td><td className="text-right font-mono text-xs">{Number(p.qty)}</td>
                    <td className="text-right font-mono text-xs">{inr(p.material_cost)}</td><td className="text-right font-mono text-xs">{inr(p.overhead_charged)}</td>
                    <td className="text-right font-mono text-xs font-semibold">{inr(p.cost_per_unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="rounded-lg bg-muted/50 p-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Overhead charged into products</span><span className="font-mono">{inr(data.overhead_charged)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Plant running costs booked to 5950 Production Overhead</span><span className="font-mono">{inr(data.overhead_actual)}</span></div>
              <div className={`mt-1 flex justify-between font-semibold ${Math.abs(diff) < 1 ? "text-foreground" : diff > 0 ? "text-rose-600 dark:text-rose-400" : "text-amber-600 dark:text-amber-400"}`}>
                <span>{diff > 0 ? "Under-charged — raise the overhead per unit" : diff < 0 ? "Over-charged — lower the overhead per unit" : "Balanced"}</span><span className="font-mono">{inr(Math.abs(diff))}</span>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Book the plant's real running costs (power, diesel, operators, repairs) to account 5950 Production Overhead via Post Journal, then compare here each month.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
